import { createContext, FC, useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { HubConnectionBuilder, HttpTransportType, HubConnection } from '@microsoft/signalr';
import { API, Endpoints } from '@brg/api';
import { useTranslation } from "react-i18next";
import { getContextFromAppName, useProfile } from "@brg/auth";
import { ApplicationShared } from "../../core";
import { getAppNameFromAppString, getCurrentAppName } from 'supporting';
import { useAuth } from "../../core/hooks/auth/useAuth";
import {getNotificationTitle} from "../components/NotificationsController/helpers/getNotificationTitle";

export enum NotificationTabs {
  all = 'all',
  activity = 'activity',
  courses = 'courses',
  services = 'services',
  archived = 'archived',
}

interface NotificationContextProps {
    connection: HubConnection | undefined;
    connectionId: string | undefined;
    notifications: any[];
    archived: any[];
    updateNotificationStatus: () => void;
    getNotifications: () => void,
    unreadCount: number;
    readLocally: any;
    currentTab: NotificationTabs,
    scroll: boolean,
    open: boolean,
    popupHeight: number,
    setCurrentTab: (tab: NotificationTabs) => void,
    getMore: () => void;
    setScroll: (val: boolean) => void,
    setOpen: (val: boolean) => void,
    setPopUpHeight: (n: number) => void;
    archiveRead: () => void;
    readNotification: (id: string) => void;
    paging: any;
}

export const NotificationContext = createContext<NotificationContextProps>({
    connection: undefined,
    connectionId: undefined,
    notifications: [],
    archived: [],
    updateNotificationStatus: () => {},
    getNotifications: () => {},
    unreadCount: 0,
    readLocally: {},
    currentTab: NotificationTabs.all,
    open: false,
    popupHeight: 0,
    setCurrentTab: (tab: NotificationTabs) => {},
    getMore: () => {},
    setScroll: (val: boolean) => {},
    setOpen: (val: boolean) => {},
    setPopUpHeight: (n: number) => {},
    scroll: false,
    archiveRead: () => {},
    readNotification: (id: string) => {},
    paging: { notifications: {}, archived: {}},
});

interface NotificationProviderProps {
    serviceId?: string;
    clientId?: string;
    accountId?: string | undefined;
    onAction?: (id: any) => void;
    onStart?: () => void;
}

const { REACT_APP_API_CONNECTION_MODE } = process.env;

const getUrl = () => {
  switch (REACT_APP_API_CONNECTION_MODE) {
    case 'production':
      return 'https://notifications.mediiia.ru';
    case 'staging':
      return 'https://notifications.burgstg.ru';
    default:
      return 'https://notifications.burgdev.ru';
  }
}

export const NotificationProvider: FC<React.PropsWithChildren<NotificationProviderProps>> = ({
    children
}) => {
    const [currentConnection, setCurrentConnection] = useState<HubConnection>();
    const [connectionId, setConnectionId] = useState<string | undefined>();
    const [notifications, setNotifications] = useState<any[]>([]);
    const [archived, setArchived] = useState<any[]>([]);
    const [paging, setPaging] = useState({ notifications: { page: 1 }, archived: { page: 1}});
    const [unreadCount, setUnreadCount] = useState(0);
    const [readLocally, setReadLocally] = useState({});
    const [currentTab, setCurrentTab] = useState(NotificationTabs.all);
    const [scroll, setScroll] = useState(false);
    const [open, setOpen] = useState(false);
    const [popupHeight, setPopUpHeight] = useState(0);
    const { i18n: { language }, t } = useTranslation();
    const {token} = useAuth();
    const profile = useProfile();

    const appName = getCurrentAppName();
    const serviceId = getAppNameFromAppString(appName);
    const clientId = getContextFromAppName(ApplicationShared.application);

    const isNotificationsSupported = useMemo(() =>
        'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window, [window, navigator])

    const connectionRef = useRef<any>();

    const getUnreadCount = useCallback(() => {
      API.request().endpoint(Endpoints.gateway)
        .url('/notification/api/Notification/count')
        .addQuery('isDelivered', 'false')
        .get()
        .release()
        .then((count) => {
            setUnreadCount(count || 0)
        });
    },[])

  const getNotifications = useCallback((page?: number) => {
    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification')
      .addQuery('limit', 20)
      .addQuery('isArchived', 'false')
      .addQuery('page', page || 1)
      .get()
      .send()
      .then((data) => data && data.json())
      .then((data =>  {

        if (!data?.data || !data?.paging) return;

        setNotifications(page === 1 ? data.data : [...notifications, ...data.data]);
        
        setPaging((prevPaging) => ({
          ...prevPaging,
          notifications: {
            ...data.paging,
          }
        }));
      }));
  },[notifications, paging])

  const getArchived = useCallback((page?: number) => {
    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification')
      .addQuery('limit', 20)
      .addQuery('isArchived', 'true')
      .addQuery('page', page || 1)
      .get()
      .send()
      .then((data) => data && data.json())
      .then((data =>  {

        if (!data?.data || !data?.paging) return;

        setArchived(page === 1 ? data.data : [...archived, ...data.data]);
        setPaging((prevPaging) => ({
          ...prevPaging,
          archived: {
            ...data.paging,
          }
        }));
      }));
  }, [archived, paging])

  const archiveRead = useCallback((page?: number) => {

      // @ts-ignore временно фильтруем уведомления чтобы не было задержек
      setNotifications(notifications.filter(item => !item?.isDelivered && !readLocally[item?.notificationId]))

    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification/ArchiveAll')
      .body({ isDelivered: true } )
      .post()
      .send()
      .then((() =>  {
        getNotifications(1);
        getArchived(1);
        getUnreadCount();
      }));
  }, [notifications, archived, paging, readLocally])

  const getMore = useCallback(() => {
    if (currentTab === NotificationTabs.archived) {
      getArchived(paging?.archived?.page + 1);
    } else {
      getNotifications(paging?.notifications?.page + 1);
    }
  }, [currentTab, getNotifications, getArchived, paging]);

    const turnMessageIntoNotification = useCallback((message: any) => {

      const translatedMessage = API.toLang(message, language);
      new Notification('', { body: getNotificationTitle(translatedMessage, t) });
    }, [language]);

    useEffect(() => {
        const connection = new HubConnectionBuilder()
            .withUrl(
                `${getUrl()}/WebSocketProvider`,
                {
                    accessTokenFactory: () => token!,
                    transport: HttpTransportType.WebSockets })
            .withAutomaticReconnect()
            .build();

        connectionRef.current = connection;

        if (profile) {
          connection
            .start()
            .then(() => {
              connection
                .invoke('GetConnectionId', serviceId, clientId, profile.accountId)
                .then((connectionId: string) => {
                  setConnectionId(connectionId);
                });
            })
            .catch(console.error);

          setCurrentConnection(connection);

          getUnreadCount();
          getNotifications(1);
          getArchived(1);

          connection.on('Receive', (message) => {
            const messageObj = API.toLang(JSON.parse(message), language);

            if (isNotificationsSupported) {
              turnMessageIntoNotification(messageObj);
            }

            setNotifications((prevNotifications) => [
                messageObj,
                ...prevNotifications,
            ]);
            getUnreadCount();
          });

          // update
          connection.on('Update', (message) => {

              // вызывается при read all
              if (!message) {
                  getNotifications(1);
                  setUnreadCount(0);
                  return;
              }
              // вызывается при read одного
              setUnreadCount(prev => prev - 1)
              getNotifications();
          })
        }

        return () => {
            void connection.stop();
        };
    }, [profile, language]);

    const readNotification = useCallback((id: string) => {
      connectionRef.current.invoke('NotificationReceived', id).then(() => {
        setReadLocally((prevState => ({
          ...prevState,
          [id]: true,
        })))
      })
    }, [connectionRef, readLocally]);

    useEffect(() => {
      if (profile && isNotificationsSupported) {
        Notification.requestPermission();
      }
    },[profile, isNotificationsSupported])

    const updateNotificationStatus = useCallback(() => {
      connectionRef.current.invoke('ReadAll', profile?.accountId).then(() => {
      })
    }, [profile]);

    const translatedNotifications = useMemo(() => {
        return API.toLang(notifications, language);
    }, [notifications, language])

    const translatedArchived = useMemo(() => {
      return API.toLang(archived, language);
    }, [archived, language])

    return (
        <NotificationContext.Provider value={{
            connection: currentConnection,
            connectionId,
            // @ts-ignore
            notifications: translatedNotifications,
            // @ts-ignore
            archived: translatedArchived,
            updateNotificationStatus,
            getNotifications,
            unreadCount,
            readLocally,
            scroll,
            open,
            popupHeight,
            setOpen,
            readNotification,
            currentTab,
            setCurrentTab,
            setScroll,
            setPopUpHeight,
            getMore,
            archiveRead,
            paging,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export function useNotificationContext() {
    return useContext(NotificationContext);
}
import { createContext, FC, useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { HubConnectionBuilder, HttpTransportType, HubConnection } from '@microsoft/signalr';
import { API, Endpoints } from '@brg/api';
import { useTranslation } from "react-i18next";
import { getContextFromAppName, useProfile } from "@brg/auth";
import { ApplicationShared } from "../../core";
import { getAppNameFromAppString, getCurrentAppName } from 'supporting';
import { useAuth } from "../../core/hooks/auth/useAuth";
import {getNotificationTitle} from "../components/NotificationsController/helpers/getNotificationTitle";

export enum NotificationTabs {
  all = 'all',
  activity = 'activity',
  courses = 'courses',
  services = 'services',
  archived = 'archived',
}

interface NotificationContextProps {
    connection: HubConnection | undefined;
    connectionId: string | undefined;
    notifications: any[];
    archived: any[];
    updateNotificationStatus: () => void;
    getNotifications: () => void,
    unreadCount: number;
    readLocally: any;
    currentTab: NotificationTabs,
    scroll: boolean,
    open: boolean,
    popupHeight: number,
    setCurrentTab: (tab: NotificationTabs) => void,
    getMore: () => void;
    setScroll: (val: boolean) => void,
    setOpen: (val: boolean) => void,
    setPopUpHeight: (n: number) => void;
    archiveRead: () => void;
    readNotification: (id: string) => void;
    paging: any;
}

export const NotificationContext = createContext<NotificationContextProps>({
    connection: undefined,
    connectionId: undefined,
    notifications: [],
    archived: [],
    updateNotificationStatus: () => {},
    getNotifications: () => {},
    unreadCount: 0,
    readLocally: {},
    currentTab: NotificationTabs.all,
    open: false,
    popupHeight: 0,
    setCurrentTab: (tab: NotificationTabs) => {},
    getMore: () => {},
    setScroll: (val: boolean) => {},
    setOpen: (val: boolean) => {},
    setPopUpHeight: (n: number) => {},
    scroll: false,
    archiveRead: () => {},
    readNotification: (id: string) => {},
    paging: { notifications: {}, archived: {}},
});

interface NotificationProviderProps {
    serviceId?: string;
    clientId?: string;
    accountId?: string | undefined;
    onAction?: (id: any) => void;
    onStart?: () => void;
}

const { REACT_APP_API_CONNECTION_MODE } = process.env;

const getUrl = () => {
  switch (REACT_APP_API_CONNECTION_MODE) {
    case 'production':
      return 'https://notifications.mediiia.ru';
    case 'staging':
      return 'https://notifications.burgstg.ru';
    default:
      return 'https://notifications.burgdev.ru';
  }
}

export const NotificationProvider: FC<React.PropsWithChildren<NotificationProviderProps>> = ({
    children
}) => {
    const [currentConnection, setCurrentConnection] = useState<HubConnection>();
    const [connectionId, setConnectionId] = useState<string | undefined>();
    const [notifications, setNotifications] = useState<any[]>([]);
    const [archived, setArchived] = useState<any[]>([]);
    const [paging, setPaging] = useState({ notifications: { page: 1 }, archived: { page: 1}});
    const [unreadCount, setUnreadCount] = useState(0);
    const [readLocally, setReadLocally] = useState({});
    const [currentTab, setCurrentTab] = useState(NotificationTabs.all);
    const [scroll, setScroll] = useState(false);
    const [open, setOpen] = useState(false);
    const [popupHeight, setPopUpHeight] = useState(0);
    const { i18n: { language }, t } = useTranslation();
    const {token} = useAuth();
    const profile = useProfile();

    const appName = getCurrentAppName();
    const serviceId = getAppNameFromAppString(appName);
    const clientId = getContextFromAppName(ApplicationShared.application);

    const isNotificationsSupported = useMemo(() =>
        'Notification' in window &&
        'serviceWorker' in navigator &&
        'PushManager' in window, [window, navigator])

    const connectionRef = useRef<any>();

    const getUnreadCount = useCallback(() => {
      API.request().endpoint(Endpoints.gateway)
        .url('/notification/api/Notification/count')
        .addQuery('isDelivered', 'false')
        .get()
        .release()
        .then((count) => {
            setUnreadCount(count || 0)
        });
    },[])

  const getNotifications = useCallback((page?: number) => {
    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification')
      .addQuery('limit', 20)
      .addQuery('isArchived', 'false')
      .addQuery('page', page || 1)
      .get()
      .send()
      .then((data) => data && data.json())
      .then((data =>  {

        if (!data?.data || !data?.paging) return;

        setNotifications(page === 1 ? data.data : [...notifications, ...data.data]);
        
        setPaging((prevPaging) => ({
          ...prevPaging,
          notifications: {
            ...data.paging,
          }
        }));
      }));
  },[notifications, paging])

  const getArchived = useCallback((page?: number) => {
    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification')
      .addQuery('limit', 20)
      .addQuery('isArchived', 'true')
      .addQuery('page', page || 1)
      .get()
      .send()
      .then((data) => data && data.json())
      .then((data =>  {

        if (!data?.data || !data?.paging) return;

        setArchived(page === 1 ? data.data : [...archived, ...data.data]);
        setPaging((prevPaging) => ({
          ...prevPaging,
          archived: {
            ...data.paging,
          }
        }));
      }));
  }, [archived, paging])

  const archiveRead = useCallback((page?: number) => {

      // @ts-ignore временно фильтруем уведомления чтобы не было задержек
      setNotifications(notifications.filter(item => !item?.isDelivered && !readLocally[item?.notificationId]))

    API.request().endpoint(Endpoints.gateway)
      .url('/notification/api/Notification/ArchiveAll')
      .body({ isDelivered: true } )
      .post()
      .send()
      .then((() =>  {
        getNotifications(1);
        getArchived(1);
        getUnreadCount();
      }));
  }, [notifications, archived, paging, readLocally])

  const getMore = useCallback(() => {
    if (currentTab === NotificationTabs.archived) {
      getArchived(paging?.archived?.page + 1);
    } else {
      getNotifications(paging?.notifications?.page + 1);
    }
  }, [currentTab, getNotifications, getArchived, paging]);

    const turnMessageIntoNotification = useCallback((message: any) => {

      const translatedMessage = API.toLang(message, language);
      new Notification('', { body: getNotificationTitle(translatedMessage, t) });
    }, [language]);

    useEffect(() => {
        const connection = new HubConnectionBuilder()
            .withUrl(
                `${getUrl()}/WebSocketProvider`,
                {
                    accessTokenFactory: () => token!,
                    transport: HttpTransportType.WebSockets })
            .withAutomaticReconnect()
            .build();

        connectionRef.current = connection;

        if (profile) {
          connection
            .start()
            .then(() => {
              connection
                .invoke('GetConnectionId', serviceId, clientId, profile.accountId)
                .then((connectionId: string) => {
                  setConnectionId(connectionId);
                });
            })
            .catch(console.error);

          setCurrentConnection(connection);

          getUnreadCount();
          getNotifications(1);
          getArchived(1);

          connection.on('Receive', (message) => {
            const messageObj = API.toLang(JSON.parse(message), language);

            if (isNotificationsSupported) {
              turnMessageIntoNotification(messageObj);
            }

            setNotifications((prevNotifications) => [
                messageObj,
                ...prevNotifications,
            ]);
            getUnreadCount();
          });

          // update
          connection.on('Update', (message) => {

              // вызывается при read all
              if (!message) {
                  getNotifications(1);
                  setUnreadCount(0);
                  return;
              }
              // вызывается при read одного
              setUnreadCount(prev => prev - 1)
              getNotifications();
          })
        }

        return () => {
            void connection.stop();
        };
    }, [profile, language]);

    const readNotification = useCallback((id: string) => {
      connectionRef.current.invoke('NotificationReceived', id).then(() => {
        setReadLocally((prevState => ({
          ...prevState,
          [id]: true,
        })))
      })
    }, [connectionRef, readLocally]);

    useEffect(() => {
      if (profile && isNotificationsSupported) {
        Notification.requestPermission();
      }
    },[profile, isNotificationsSupported])

    const updateNotificationStatus = useCallback(() => {
      connectionRef.current.invoke('ReadAll', profile?.accountId).then(() => {
      })
    }, [profile]);

    const translatedNotifications = useMemo(() => {
        return API.toLang(notifications, language);
    }, [notifications, language])

    const translatedArchived = useMemo(() => {
      return API.toLang(archived, language);
    }, [archived, language])

    return (
        <NotificationContext.Provider value={{
            connection: currentConnection,
            connectionId,
            // @ts-ignore
            notifications: translatedNotifications,
            // @ts-ignore
            archived: translatedArchived,
            updateNotificationStatus,
            getNotifications,
            unreadCount,
            readLocally,
            scroll,
            open,
            popupHeight,
            setOpen,
            readNotification,
            currentTab,
            setCurrentTab,
            setScroll,
            setPopUpHeight,
            getMore,
            archiveRead,
            paging,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export function useNotificationContext() {
    return useContext(NotificationContext);
}
